import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/toaster';
import { useToast } from './components/ui/use-toast';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MobileEncargadoPage from './pages/MobileEncargadoPage';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Redirect preserving the ?redirect= param after login
function LoginRedirect() {
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/';
  return <Navigate to={redirectTo} replace />;
}

// Auth Context
const AuthContext = React.createContext(null);

export function useAuth() {
  return React.useContext(AuthContext);
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('auth_token');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Main App Content with Router
function AppRoutes() {
  // Lazy init: read localStorage synchronously — no useEffect/setState needed
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try { return JSON.parse(userData); } catch { return null; }
    }
    return null;
  });
  const { toast } = useToast();

  const handleLogin = (userData, token, refreshToken) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_data', JSON.stringify(userData));
    // Legacy keys for mobile page
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    // JWT refresh token
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    setUser(userData);
    toast({
      title: 'Sesión iniciada',
      description: `Bienvenido/a ${userData.nombre}`,
    });
  };

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    try {
      const { authAPI } = await import('./lib/api');
      await authAPI.logout(refreshToken);
    } catch (_e) {
      // best-effort logout API call
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast({
      title: 'Sesión cerrada',
      description: 'Has cerrado sesión correctamente',
    });
  };

  if (false) {
    // loading spinner - kept for structure, no longer needed (lazy init)
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, handleLogin, handleLogout }}>
      <Routes>
        {/* Login Route */}
        <Route 
          path="/login" 
          element={
            user ? <LoginRedirect /> : <LoginPage onLogin={handleLogin} />
          } 
        />
        
        {/* Main Dashboard */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <DashboardPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          } 
        />
        
        {/* Mobile Encargado View */}
        <Route 
          path="/mobile" 
          element={<MobileEncargadoPage />} 
        />
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
        <Toaster />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
